import type { SellChannel } from '@/types/sell-channel';

/**
 * Whether the confirm dialog may OFFER to send the customer a status-change WhatsApp — and never
 * whether it should send one on its own. §7 / D8 of the commerce epic:
 *
 *   - Default status-change sends are **removed** for every order of a brand with no POS provider;
 *     a **paid opt-in** restores them per tenant.
 *   - A web order gets exactly ONE WhatsApp (the placement confirmation with the tracking link);
 *     after that "the website is the status surface". The same holds for an external-agent order,
 *     which we do not own the thread of at all.
 *
 * This UI cannot read the opt-in: it lives on the munchies-api master client, and agent-ui only
 * ever talks to menteo-agent (`CLAUDE.md`). So the rule here is the conservative half of the spec —
 * never send by default, and do not even show the composer for an order whose status surface is the
 * tracking page. The operator's explicit per-confirmation tick is what still sends a WhatsApp
 * message on a WhatsApp order, which is the one case §7 leaves to the operator's judgement.
 *
 * ── How this is verified ──────────────────────────────────────────────────────────────────────
 * menteo-agent-ui has no test runner (`CLAUDE.md`: "There is no test suite"), and adding one is
 * refactor #2 of the audit's backlog — an owner decision, not a side effect of this change. The
 * rule is therefore isolated here as a pure, dependency-free function so that:
 *   1. it is reviewable in one screen, against the spec quoted above;
 *   2. `npm run build` type-checks its exhaustiveness over `SellChannel`;
 *   3. the day vitest lands, `status-message-offer.spec.ts` is the first file to write — the table
 *      is four rows: WHATSAPP+phone → true, WHATSAPP without phone → false, WEB → false,
 *      EXTERNAL_AGENT → false, plus unknown channel → false.
 * Every caller must go through it rather than re-deriving the condition inline.
 */
export interface StatusMessageTarget {
  /** The order's sell channel. Undefined when the surface does not know it — treated as "not WhatsApp". */
  channel?: SellChannel | null;
  /** The customer's phone, when the surface has it. No phone, nothing to send to. */
  phone?: string | null;
}

export function statusMessageOffer(target: StatusMessageTarget): boolean {
  if (!target.phone) return false;
  // Deliberately an equality and not a `!== 'WEB'`: an unknown/absent channel must fall on the
  // silent side, and a fourth channel added tomorrow must too until someone decides otherwise.
  return target.channel === 'WHATSAPP';
}
